import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { getSubscriptionExpiryAlert } from "../../api/subscription";
import SubscriptionExpiryModal from "../subscription/SubscriptionExpiryModal";

// Dismissal is tracked per user + expiry date + phase, so the banner shows again
// once a new expiry window approaches (e.g. after a renewal) but stays hidden after
// the user acknowledges it for the current cycle. This gives the "show once per user
// belonging to the mother user" behaviour — every user under the owner sees it.
//
// The phase is critical: `expires_at` stays fixed as the subscription escalates
// (active warning -> final day -> grace period), so keying on expires_at alone would
// let a single dismissal during the early warning permanently suppress the much more
// urgent grace-period alert. Including the phase makes each escalation re-show the
// modal once, while a dismissal within the same phase keeps it hidden.
const DISMISS_KEY_PREFIX = "slapie_sub_expiry_dismissed";

// Derive the alert "phase" so escalating states re-surface the banner.
const getAlertPhase = (alertData) => {
  if (!alertData) return "none";
  if (alertData.in_grace_period) return "grace";
  if (alertData.days_left <= 1) return "final";
  return "warning";
};

const buildDismissKey = (username, expiresAt, phase) =>
  `${DISMISS_KEY_PREFIX}:${username || "anon"}:${expiresAt || ""}:${phase || "warning"}`;

export default function SubscriptionExpiryManager() {
  const user = useSelector((state) => state.auth.user);
  const history = useHistory();

  const [alertData, setAlertData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setOpen(false);
      setAlertData(null);
      return;
    }

    (async () => {
      try {
        const { data } = await getSubscriptionExpiryAlert();
        if (cancelled || !data || !data.should_alert) {
          return;
        }

        const dismissKey = buildDismissKey(
          user.username,
          data.expires_at,
          getAlertPhase(data)
        );
        if (localStorage.getItem(dismissKey) === "true") {
          return;
        }

        setAlertData(data);
        setOpen(true);
      } catch (error) {
        // Non-critical banner: stay silent if the check fails.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const dismiss = useCallback(() => {
    if (alertData) {
      localStorage.setItem(
        buildDismissKey(
          user?.username,
          alertData.expires_at,
          getAlertPhase(alertData)
        ),
        "true"
      );
    }
    setOpen(false);
  }, [alertData, user]);

  const handleRenew = () => {
    dismiss();
    history.push("/minha-assinatura");
  };

  if (!alertData) {
    return null;
  }

  return (
    <SubscriptionExpiryModal
      open={open}
      alertData={alertData}
      onRenew={handleRenew}
      onClose={dismiss}
    />
  );
}
